import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import Sidebar from '../Shared/Sidebar';
import Icon from '../Shared/Icons';
import { RATING_CATEGORIES, categoryInfo } from '../../config/ratingCategories';
import {
  searchAllPlayers, filterPlayers, ensureIfrPlayer, quickFixPlayer,
  groupByClub, resolvePastedNames,
} from '../../utils/playerSearch';
import {
  fetchLiveEvents, createDraftLiveEvent,
  addParticipantToDraft, addManyParticipantsToDraft, removeParticipantFromDraft,
  launchLiveEvent, addParticipantLive,
  computeLiveStandings, generateNextSwissRound, saveRound, advanceKnockout,
  declareLiveWinner, deleteLiveEvent, computeLiveProjections, finalizeLiveEvent,
  addHelper, removeHelper,
} from '../../utils/liveEvent';
import { resolveAuthorName } from '../../utils/programData';
import { fmtDate } from '../../utils/weekUtils';
import { T } from '../Programs/ui';

const RESULTS = [
  { v: '', l: 'Bado' },
  { v: '1-0', l: 'Mweupe' },
  { v: '½-½', l: 'Sare' },
  { v: '0-1', l: 'Mweusi' },
];

const PAIRING_SYSTEMS = [
  { key: 'swiss', label: 'Swiss', desc: 'Kwa vikundi vikubwa (hadi mamia) — pointi zinazofanana zinacheza pamoja', icon: 'sparkle' },
  { key: 'roundRobin', label: 'Round Robin', desc: 'Kila mtu na kila mtu — vikundi vidogo (watu 4–16)', icon: 'flag' },
  { key: 'knockout', label: 'Knockout', desc: 'Kuondoana — mshindi mmoja tu anaendelea kila raundi', icon: 'trophy' },
];

const L = {
  page: { display: 'flex', minHeight: '100vh', background: T.bg, fontFamily: T.sans },
  main: { flex: 1, padding: '26px 20px 60px', overflowY: 'auto', width: '100%' },
  hero: { background: 'linear-gradient(135deg, #0B3524, #175239)', color: '#fff', borderRadius: '20px', padding: '26px 24px', marginBottom: '18px' },
  card: { background: '#fff', borderRadius: '16px', padding: '20px', border: `1px solid ${T.line}`, marginBottom: '16px' },
  h2: { fontFamily: T.serif, fontSize: '18px', color: T.green, margin: '0 0 14px' },
  label: { display: 'block', fontSize: '12.5px', fontWeight: 700, color: '#374151', marginBottom: '6px' },
  input: { width: '100%', padding: '11px 13px', borderRadius: '10px', border: `1px solid ${T.line}`, fontSize: '14px', fontFamily: 'inherit', background: '#FCFBF8', boxSizing: 'border-box' },
  btn: { background: `linear-gradient(135deg, ${T.green}, ${T.greenLight})`, color: '#fff', border: 'none', padding: '13px 20px', borderRadius: '12px', fontSize: '13.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  btnGhost: { background: '#fff', color: T.green, border: `1px solid ${T.line}`, padding: '11px 16px', borderRadius: '11px', fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
  pill: { display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 11px', borderRadius: '99px', fontSize: '11px', fontWeight: 700 },
  empty: { textAlign: 'center', padding: '36px 20px', color: T.muted, fontSize: '13px' },
  err: { background: '#FEF2F2', color: '#991B1B', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' },
  ok: { background: '#ECFDF5', color: '#065F46', padding: '12px 14px', borderRadius: '10px', fontSize: '13px', marginBottom: '14px' },
};

export default function LiveEvent() {
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();
  const isAdmin = userRole === 'admin';

  const [view, setView] = useState('events'); // 'events' | 'winners'
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function load() {
    setLoading(true);
    try {
      const es = await fetchLiveEvents();
      setEvents(es);
      setOpenId(prev => prev || (es[0]?.id ?? null));
    } catch (e) { console.error(e); setErr('Imeshindikana kupakia matukio.'); }
    setLoading(false);
  }

  function flash(m) { setMsg(m); setTimeout(() => setMsg(''), 4000); }
  const active = events.find(e => e.id === openId);
  const isReferee = !!active && active.refereeId === currentUser?.uid;
  const isHelper = !!active && (active.helperIds || []).includes(currentUser?.uid);
  const canManage = isAdmin || isReferee || isHelper;

  return (
    <div style={L.page}>
      <Sidebar />
      <div style={L.main}>

        <div style={L.hero}>
          <div style={{ fontSize: '10.5px', letterSpacing: '2px', opacity: 0.7, fontWeight: 800 }}>IYANNA FOUNDATION RATING</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '12px', marginTop: '9px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontFamily: T.serif, fontSize: '26px' }}>Tukio la Papo Hapo</div>
              <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '7px' }}>
                Simamia mashindano hadi mshindi — Swiss, Round Robin au Knockout — yaunganishwe na IFR moja kwa moja
              </div>
            </div>
            {isAdmin && view === 'events' && (
              <button onClick={() => setShowCreate(!showCreate)} style={{
                background: showCreate ? 'rgba(255,255,255,0.15)' : '#fff', color: showCreate ? '#fff' : T.green,
                border: 'none', padding: '12px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
              }}>
                {showCreate ? 'Funga' : '+ Tukio Jipya'}
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '7px', marginBottom: '16px' }}>
          {[{ k: 'events', l: 'Matukio' }, { k: 'winners', l: '🏆 Washindi' }].map(t => (
            <button key={t.k} onClick={() => setView(t.k)} style={{
              flex: 1, padding: '11px', borderRadius: '11px', cursor: 'pointer',
              border: `1px solid ${view === t.k ? T.green : T.line}`,
              background: view === t.k ? T.green : '#fff', color: view === t.k ? '#fff' : T.muted,
              fontSize: '13px', fontWeight: 700, fontFamily: 'inherit',
            }}>{t.l}</button>
          ))}
        </div>

        {msg && <div style={L.ok}>{msg}</div>}
        {err && <div style={L.err}>{err}</div>}

        {view === 'winners' ? (
          <WinnersList events={events.filter(e => e.status === 'finished')} navigate={navigate} />
        ) : (
          <>
            <div style={{ ...L.card, background: '#FAF8F3', fontSize: '12px', color: T.muted, lineHeight: 1.7 }}>
              <Icon name="info" size={12} color={T.orange} /> Rating rasmi ya wachezaji <strong>haiguswi</strong> mpaka
              tukio limalizike na admin abofye "Maliza na Uhamishe kwenye IFR", kisha aende
              "Usimamizi wa IFR → Kuchapisha" kuhakiki na kuthibitisha rasmi. Namba unazoziona hapa wakati
              tukio linaendelea ni <strong>makadirio</strong> tu.
            </div>

            {!isAdmin && (
              <div style={{ ...L.card, background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#1E3A8A', fontSize: '12.5px' }}>
                <Icon name="info" size={13} color="#1D4ED8" /> Unaona kama mtazamaji.
                {canManage ? ' Umeteuliwa kusaidia kuendesha tukio hili.' : ' Admin ndiye kiongozi, au anaweza kukuongeza kama msaidizi.'}
              </div>
            )}

            {showCreate && isAdmin && (
              <CreateDraft currentUser={currentUser} onDone={(id) => { setShowCreate(false); load(); setOpenId(id); flash('Rasimu ya tukio imeanzishwa — anza kuongeza washindani.'); }} onError={setErr} />
            )}

            {loading ? (
              <div style={{ ...L.card, ...L.empty }}>Inapakia...</div>
            ) : events.length === 0 ? (
              <div style={{ ...L.card, ...L.empty }}>
                {isAdmin ? 'Bado hakuna tukio. Bofya "+ Tukio Jipya" kuanza.' : 'Bado hakuna tukio lililoanzishwa.'}
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '7px', marginBottom: '16px', overflowX: 'auto' }}>
                  {events.map(e => {
                    const cat = categoryInfo(e.category);
                    return (
                      <button key={e.id} onClick={() => setOpenId(e.id)} style={{
                        whiteSpace: 'nowrap', padding: '9px 16px', borderRadius: '99px', cursor: 'pointer',
                        border: `1px solid ${openId === e.id ? cat.color : T.line}`,
                        background: openId === e.id ? cat.color : '#fff', color: openId === e.id ? '#fff' : T.muted,
                        fontSize: '12.5px', fontWeight: 700, fontFamily: 'inherit',
                      }}>
                        {e.title} {e.status === 'draft' ? '📝' : e.status === 'finished' ? '🏆' : ''}
                      </button>
                    );
                  })}
                </div>
                {active && (
                  active.status === 'draft' ? (
                    <DraftRoster key={active.id} event={active} isAdmin={isAdmin}
                      currentUser={currentUser} onChange={load} flash={flash} setErr={setErr} />
                  ) : (
                    <RunningEvent key={active.id} event={active} isAdmin={isAdmin} canManage={canManage}
                      currentUser={currentUser} onChange={load} flash={flash} setErr={setErr} navigate={navigate} />
                  )
                )}
              </>
            )}
          </>
        )}

      </div>
    </div>
  );
}

// ══ WASHINDI — sehemu maalum ya kuonyesha washindi wa mashindano ═══════
function WinnersList({ events, navigate }) {
  const sorted = [...events].sort((a, b) => (b.finishedAt?.seconds || 0) - (a.finishedAt?.seconds || 0));
  if (sorted.length === 0) {
    return <div style={{ ...L.card, ...L.empty }}>Bado hakuna mashindano yaliyomalizika na kupata mshindi.</div>;
  }
  return (
    <>
      {sorted.map(e => {
        const cat = categoryInfo(e.category);
        return (
          <div key={e.id} onClick={() => navigate(`/rating/live/${e.id}/print`)}
            style={{ ...L.card, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '46px', height: '46px', borderRadius: '13px', flexShrink: 0,
              background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '21px',
            }}>🏆</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontFamily: T.serif, fontSize: '16px', color: T.green }}>{e.title}</div>
              <div style={{ fontSize: '12px', color: T.muted, marginTop: '3px' }}>
                Mshindi: <strong style={{ color: T.ink }}>{e.winnerName}</strong>
                {' · '}<span style={{ ...L.pill, color: cat.color, background: `${cat.color}15`, padding: '2px 8px' }}>{cat.label}</span>
                {e.finishedAt && <> · {fmtDate(e.finishedAt)}</>}
              </div>
            </div>
            <span style={{ color: T.muted }}>›</span>
          </div>
        );
      })}
    </>
  );
}

// ══ KUTENGENEZA RASIMU — bila washindani; hao wanaongezwa baadaye ═════
function CreateDraft({ currentUser, onDone, onError }) {
  const [title, setTitle] = useState(`Mashindano — ${fmtDate(new Date())}`);
  const [category, setCategory] = useState('classical');
  const [pairingSystem, setPairingSystem] = useState('swiss');
  const [totalRounds, setTotalRounds] = useState(5);
  const [isBootstrap, setIsBootstrap] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!title.trim()) { onError('Weka jina la tukio.'); return; }
    setBusy(true); onError('');
    try {
      const authorName = await resolveAuthorName(currentUser);
      const id = await createDraftLiveEvent({
        title: title.trim(), category, pairingSystem,
        totalRounds: pairingSystem === 'knockout' ? null : totalRounds,
        isBootstrap,
        author: { uid: currentUser.uid, name: authorName },
      });
      onDone(id);
    } catch (e) { console.error(e); onError(e.message || 'Imeshindikana kutengeneza tukio.'); }
    setBusy(false);
  }

  return (
    <div style={L.card}>
      <h2 style={L.h2}>Tukio Jipya</h2>

      <label style={L.label}>Jina la tukio</label>
      <input style={{ ...L.input, marginBottom: '16px' }} value={title} onChange={e => setTitle(e.target.value)} />

      <label style={L.label}>Kasi (rating itakayoathiriwa)</label>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {RATING_CATEGORIES.map(c => (
          <button key={c.key} onClick={() => setCategory(c.key)} style={{
            flex: '1 1 100px', padding: '11px', borderRadius: '11px', cursor: 'pointer', textAlign: 'left',
            border: `1px solid ${category === c.key ? c.color : T.line}`,
            background: category === c.key ? c.color : '#fff', color: category === c.key ? '#fff' : T.ink,
            fontFamily: 'inherit',
          }}>
            <div style={{ fontSize: '13px', fontWeight: 800 }}>{c.label}</div>
            <div style={{ fontSize: '10px', opacity: 0.85, marginTop: '3px' }}>{c.description}</div>
          </button>
        ))}
      </div>

      <label style={L.label}>Mfumo wa kupanga mechi</label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
        {PAIRING_SYSTEMS.map(p => (
          <button key={p.key} onClick={() => setPairingSystem(p.key)} style={{
            display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 14px', borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
            border: `1px solid ${pairingSystem === p.key ? T.green : T.line}`,
            background: pairingSystem === p.key ? `${T.green}0C` : '#fff', fontFamily: 'inherit',
          }}>
            <Icon name={p.icon} size={17} color={T.green} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '13px', fontWeight: 800, color: T.ink }}>{p.label}</div>
              <div style={{ fontSize: '11px', color: T.muted, marginTop: '2px' }}>{p.desc}</div>
            </div>
            {pairingSystem === p.key && <Icon name="check" size={16} color={T.green} />}
          </button>
        ))}
      </div>

      {pairingSystem === 'swiss' && (
        <div style={{ ...L.card, background: '#FAF8F3', marginBottom: '16px', fontSize: '11.5px', color: T.muted, lineHeight: 1.7 }}>
          Chaguo bora kwa mashindano makubwa (mf. wanafunzi 400) — hakuna haja ya kila mtu kucheza na kila mtu.
        </div>
      )}

      {pairingSystem !== 'knockout' && (
        <>
          <label style={L.label}>Idadi ya raundi</label>
          <input type="number" min={1} max={20} style={{ ...L.input, marginBottom: '16px', maxWidth: '140px' }}
            value={totalRounds}
            onChange={e => setTotalRounds(Number(e.target.value) || 1)} />
        </>
      )}

      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '9px', cursor: 'pointer', fontSize: '12.5px', color: '#374151', marginBottom: '18px' }}>
        <input type="checkbox" checked={isBootstrap} onChange={e => setIsBootstrap(e.target.checked)} style={{ width: '17px', height: '17px', marginTop: '2px' }} />
        <span>Tukio la msingi (hakuna mchezaji mwenye rating kwenye kasi hii). Kila mtu atahesabiwa kama 1200 — haliwezi kutoa norm.</span>
      </label>

      <button style={{ ...L.btn, opacity: busy ? 0.6 : 1, width: '100%' }} disabled={busy} onClick={submit}>
        {busy ? 'Inatengeneza…' : 'Anzisha Rasimu — ongeza washindani baadaye'}
      </button>
    </div>
  );
}

// ══ ROSTA YA RASIMU — kuongeza washindani, kila mmoja anahifadhiwa papo
//    hapo. Hii ndiyo "draft isiyopotea" iliyoombwa. ════════════════════
function DraftRoster({ event, isAdmin, currentUser, onChange, flash, setErr }) {
  const [search, setSearch] = useState('');
  const [allPlayers, setAllPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pastePreview, setPastePreview] = useState(null);
  const [launching, setLaunching] = useState(false);

  useEffect(() => {
    searchAllPlayers().then(setAllPlayers).catch(console.error).finally(() => setLoadingPlayers(false));
  }, []);

  const picked = event.participants || [];
  const pickedIds = new Set(picked.map(p => p.id));
  const results = filterPlayers(allPlayers, search).filter(p => !pickedIds.has(p.id));
  const grouped = groupByClub(results.slice(0, 200));
  const pickedGrouped = groupByClub(picked);

  async function addOne(p) {
    setBusyId(`${p.source}-${p.id}`); setErr('');
    try {
      const id = await ensureIfrPlayer(p);
      await addParticipantToDraft(event.id, { id, name: p.fullName });
      setSearch('');
      onChange();
    } catch (e) { console.error(e); setErr(e.message || 'Imeshindikana kuongeza mchezaji.'); }
    setBusyId(null);
  }

  async function removeOne(id) {
    try { await removeParticipantFromDraft(event.id, id); onChange(); }
    catch (e) { setErr(e.message || 'Imeshindikana kuondoa.'); }
  }

  function previewPaste() {
    const { matched, unmatched } = resolvePastedNames(pasteText, allPlayers.filter(p => !pickedIds.has(p.id)));
    setPastePreview({ matched, unmatched });
  }

  async function confirmPaste() {
    if (!pastePreview) return;
    setErr('');
    try {
      const toAdd = [];
      for (const p of pastePreview.matched) {
        const id = await ensureIfrPlayer(p);
        toAdd.push({ id, name: p.fullName });
      }
      for (const name of pastePreview.unmatched) {
        const id = await ensureIfrPlayer({ source: 'new', fullName: name });
        toAdd.push({ id, name });
      }
      const out = await addManyParticipantsToDraft(event.id, toAdd);
      flash(`Washindani ${out.added} wameongezwa${out.skipped ? ` (${out.skipped} walikuwepo tayari)` : ''}.`);
      setPasteText(''); setPastePreview(null); setShowPaste(false);
      onChange();
    } catch (e) { console.error(e); setErr(e.message || 'Imeshindikana kuongeza orodha.'); }
  }

  async function doLaunch() {
    if (picked.length < 2) { setErr('Ongeza angalau washindani 2 kabla ya kuanzisha.'); return; }
    if (!window.confirm(`Anzisha "${event.title}" na washindani ${picked.length}? Baada ya hapa, orodha ya washindani (kwa Round Robin/Knockout) haiwezi kubadilishwa.`)) return;
    setLaunching(true); setErr('');
    try {
      await launchLiveEvent(event.id);
      flash('Mashindano yameanzishwa rasmi! 🎉');
      onChange();
    } catch (e) { console.error(e); setErr(e.message || 'Imeshindikana kuanzisha.'); }
    setLaunching(false);
  }

  return (
    <>
      <div style={{ ...L.card, borderLeft: `4px solid ${T.orange}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: T.serif, fontSize: '19px', color: T.green }}>{event.title}</div>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '4px' }}>
              {categoryInfo(event.category).label} · {PAIRING_SYSTEMS.find(p => p.key === event.pairingSystem)?.label}
            </div>
          </div>
          <span style={{ ...L.pill, color: '#B45309', background: '#FEF3C7' }}>📝 RASIMU — bado haijaanza</span>
        </div>
        <div style={{ fontSize: '12px', color: T.muted, marginTop: '12px', lineHeight: 1.7 }}>
          Tukio hili ni <strong>rasimu</strong> — kila mchezaji unayemwongeza anahifadhiwa papo hapo. Ukifunga
          kivinjari au mtandao ukakatika, hakuna anayepotea; rudi hapa wakati wowote kuendelea pale ulipoishia.
        </div>
      </div>

      {isAdmin && (
        <div style={{ ...L.card, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ fontSize: '13px', color: T.ink }}>
            Washindani <strong>{picked.length}</strong> wameongezwa.
          </div>
          <button style={{ ...L.btn, opacity: launching ? 0.6 : 1 }} disabled={launching} onClick={doLaunch}>
            {launching ? 'Inaanzisha…' : '🚀 Anzisha Mashindano Rasmi'}
          </button>
        </div>
      )}

      <div style={L.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h2 style={{ ...L.h2, marginBottom: 0 }}>Ongeza Washindani</h2>
          <button style={L.btnGhost} onClick={() => setShowPaste(!showPaste)}>
            {showPaste ? 'Funga' : '📋 Bandika Orodha'}
          </button>
        </div>

        {showPaste && (
          <div style={{ background: '#FAF8F3', border: `1px solid ${T.line}`, borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ fontSize: '11.5px', color: T.muted, marginBottom: '9px', lineHeight: 1.6 }}>
              Bandika majina — mstari mmoja kwa kila jina (kutoka Excel au daftari). Majina yanayofanana na
              waliosajiliwa tayari (IFR au Chess Club) yataunganishwa; mapya kabisa yataongezwa kwa jina tu.
            </div>
            <textarea
              style={{ ...L.input, minHeight: '140px', marginBottom: '10px', resize: 'vertical', lineHeight: 1.7 }}
              value={pasteText}
              onChange={e => { setPasteText(e.target.value); setPastePreview(null); }}
              placeholder={'Juma Hassan\nAisha Mohamed\nNeema John\n...'}
            />
            {!pastePreview ? (
              <button style={L.btn} onClick={previewPaste} disabled={!pasteText.trim()}>Hakiki Orodha</button>
            ) : (
              <>
                <div style={{ fontSize: '12.5px', color: T.ink, marginBottom: '10px', lineHeight: 1.8 }}>
                  ✓ Watapatikana (wanaunganishwa): <strong>{pastePreview.matched.length}</strong><br />
                  + Majina mapya kabisa (yataongezwa mapya): <strong>{pastePreview.unmatched.length}</strong>
                </div>
                {pastePreview.unmatched.length > 0 && (
                  <div style={{ fontSize: '11.5px', color: T.muted, marginBottom: '10px' }}>
                    Mapya: {pastePreview.unmatched.join(', ')}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button style={L.btn} onClick={confirmPaste}>
                    Ongeza Wote ({pastePreview.matched.length + pastePreview.unmatched.length})
                  </button>
                  <button style={L.btnGhost} onClick={() => setPastePreview(null)}>Ghairi</button>
                </div>
              </>
            )}
          </div>
        )}

        <input style={{ ...L.input, marginBottom: '9px' }} placeholder="Tafuta jina — kutoka IFR au Chess Club..."
          value={search} onChange={e => setSearch(e.target.value)} />

        {search.trim() && (
          <div style={{ border: `1px solid ${T.line}`, borderRadius: '11px', marginBottom: '6px', maxHeight: '320px', overflowY: 'auto' }}>
            {loadingPlayers ? (
              <div style={{ padding: '13px', fontSize: '12.5px', color: T.muted }}>Inapakia...</div>
            ) : grouped.length === 0 ? (
              <div style={{ padding: '13px', fontSize: '12.5px', color: T.muted }}>Hakuna aliyepatikana kwa jina hilo.</div>
            ) : grouped.map(group => (
              <div key={group.clubName}>
                <div style={{
                  padding: '7px 13px', fontSize: '10.5px', fontWeight: 800, color: T.muted,
                  background: '#FAF8F3', textTransform: 'uppercase', letterSpacing: '0.4px',
                }}>{group.clubName} ({group.members.length})</div>
                {group.members.map(p => (
                  <PlayerSearchRow key={`${p.source}-${p.id}`} p={p}
                    busy={busyId === `${p.source}-${p.id}`}
                    onAdd={() => addOne(p)} onEdit={() => setEditingPlayer(p)} />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {editingPlayer && (
        <QuickFixPanel player={editingPlayer} onClose={() => setEditingPlayer(null)} onSaved={(patch) => {
          setAllPlayers(list => list.map(x => x.id === editingPlayer.id && x.source === editingPlayer.source ? { ...x, ...patch } : x));
          setEditingPlayer(null);
        }} />
      )}

      <div style={L.card}>
        <h2 style={L.h2}>Washindani Walioongezwa ({picked.length})</h2>
        {picked.length === 0 ? (
          <div style={L.empty}>Bado hakuna aliyeongezwa. Tumia utafutaji hapo juu au "Bandika Orodha".</div>
        ) : pickedGrouped.map(group => (
          <div key={group.clubName} style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', fontWeight: 800, color: T.muted, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
              {group.clubName} ({group.members.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
              {group.members.map(p => (
                <span key={p.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '7px', background: `${T.green}12`, color: T.green,
                  padding: '6px 11px', borderRadius: '99px', fontSize: '12px', fontWeight: 700,
                }}>
                  {p.name}
                  <span onClick={() => removeOne(p.id)} style={{ cursor: 'pointer', color: '#DC2626', fontWeight: 800 }}>✕</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function PlayerSearchRow({ p, onAdd, onEdit, busy }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 13px', borderTop: `1px solid #F3F1EB` }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '9px', flexShrink: 0,
        background: p.source === 'ifr' ? '#ECFDF5' : '#FFF7ED', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '12px', fontWeight: 800, color: p.source === 'ifr' ? '#059669' : '#C2410C',
      }}>
        {(p.fullName || '?').charAt(0).toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 700, color: T.ink }}>{p.fullName}</div>
        <div style={{ fontSize: '10.5px', color: T.muted, marginTop: '2px' }}>
          {p.school || '—'} {p.ifr ? `· IFR ${p.ifr}` : p.source === 'ifr' ? '· bado hana rating' : '· Chess Club'}
        </div>
      </div>
      <button type="button" onClick={onEdit} style={{ ...L.btnGhost, padding: '6px 10px', fontSize: '11px' }}>Hariri</button>
      <button type="button" disabled={busy} onClick={onAdd} style={{ ...L.btn, padding: '7px 13px', fontSize: '11.5px', opacity: busy ? 0.6 : 1 }}>
        {busy ? '…' : '+ Ongeza'}
      </button>
    </div>
  );
}

function QuickFixPanel({ player, onClose, onSaved }) {
  const [name, setName] = useState(player.fullName);
  const [school, setSchool] = useState(player.school || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function save() {
    setBusy(true); setErr('');
    try {
      await quickFixPlayer(player, { fullName: name.trim(), school: school.trim() });
      onSaved({ fullName: name.trim(), school: school.trim() });
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi.'); }
    setBusy(false);
  }

  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '12px', padding: '14px', marginBottom: '14px' }}>
      <div style={{ fontSize: '12.5px', fontWeight: 800, color: '#92400E', marginBottom: '10px' }}>
        Rekebisha taarifa za {player.fullName} — hapa hapa, bila kutoka
      </div>
      {err && <div style={{ ...L.err, marginBottom: '9px' }}>{err}</div>}
      <label style={L.label}>Jina kamili</label>
      <input style={{ ...L.input, marginBottom: '9px' }} value={name} onChange={e => setName(e.target.value)} />
      <label style={L.label}>Shule / eneo</label>
      <input style={{ ...L.input, marginBottom: '12px' }} value={school} onChange={e => setSchool(e.target.value)} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ ...L.btn, padding: '9px 15px', fontSize: '12px', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={save}>Hifadhi</button>
        <button style={{ ...L.btnGhost, padding: '9px 15px', fontSize: '12px' }} onClick={onClose}>Ghairi</button>
      </div>
    </div>
  );
}

// ══ TUKIO LINALOENDELEA (au lililomalizika) ════════════════════════════
function RunningEvent({ event, isAdmin, canManage, currentUser, onChange, flash, setErr, navigate }) {
  const cat = categoryInfo(event.category);
  const pairingInfo = PAIRING_SYSTEMS.find(p => p.key === event.pairingSystem);
  const standings = computeLiveStandings(event);
  const rounds = event.rounds || [];
  const latestRound = rounds[rounds.length - 1];
  const isKnockout = event.pairingSystem === 'knockout';
  const isFinal = isKnockout && latestRound && latestRound.pairings.length === 1 && !latestRound.pairings[0].isBye;

  const [pairs, setPairs] = useState(null);
  const [byeId, setByeId] = useState('');
  const [busy, setBusy] = useState(false);
  const [showWinner, setShowWinner] = useState(false);
  const [winnerPick, setWinnerPick] = useState('');
  const [showFinalize, setShowFinalize] = useState(false);
  const [arbiterName, setArbiterName] = useState('');
  const [venue, setVenue] = useState('');
  const [finalizeResult, setFinalizeResult] = useState(null);
  const [projections, setProjections] = useState(null);
  const [loadingProj, setLoadingProj] = useState(false);
  const [showHelpers, setShowHelpers] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addAllPlayers, setAddAllPlayers] = useState([]);
  const [addBusy, setAddBusy] = useState(false);

  async function openAddPlayer() {
    setShowAddPlayer(true);
    if (addAllPlayers.length) return;
    try { setAddAllPlayers(await searchAllPlayers()); }
    catch (e) { console.error(e); setErr('Imeshindikana kupakia orodha ya wachezaji.'); }
  }

  async function addLatePlayer(p) {
    setAddBusy(true); setErr('');
    try {
      const id = await ensureIfrPlayer(p);
      await addParticipantLive(event.id, { id, name: p.fullName });
      setAddSearch(''); setShowAddPlayer(false);
      flash(`${p.fullName} ameongezwa kwenye tukio.`);
      onChange();
    } catch (e) { setErr(e.message || 'Imeshindikana kuongeza mchezaji.'); }
    setAddBusy(false);
  }

  async function loadProjections() {
    setLoadingProj(true);
    try { setProjections(await computeLiveProjections(event)); }
    catch (e) { console.error(e); }
    setLoadingProj(false);
  }

  function planSwissRound() {
    const out = generateNextSwissRound(event);
    if (out.error) { setErr(out.error); return; }
    setPairs(out.pairings.map(p => ({ ...p })));
    setByeId(out.byeId || '');
    setErr('');
  }

  function updatePair(i, k, v) {
    setPairs(list => list.map((p, j) => j === i ? { ...p, [k]: v } : p));
  }

  async function saveSwissRound() {
    setBusy(true); setErr('');
    try {
      await saveRound(event.id, { pairings: pairs, byeId: byeId || null, byeName: byeId ? event.participants.find(p => p.id === byeId)?.name : null });
      setPairs(null); setByeId('');
      flash('Raundi imechapishwa.');
      onChange();
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi raundi.'); }
    setBusy(false);
  }

  async function saveResultsForRound(roundNumber, updatedPairings) {
    setBusy(true); setErr('');
    try {
      await saveRound(event.id, { ...rounds.find(r => r.number === roundNumber), pairings: updatedPairings }, roundNumber);
      flash('Matokeo yamehifadhiwa.');
      onChange();
    } catch (e) { console.error(e); setErr('Imeshindikana kuhifadhi matokeo.'); }
    setBusy(false);
  }

  async function doAdvanceKnockout() {
    setBusy(true); setErr('');
    try { await advanceKnockout(event.id); flash('Raundi inayofuata imepangwa.'); onChange(); }
    catch (e) { setErr(e.message || 'Imeshindikana kuendelea.'); }
    setBusy(false);
  }

  async function confirmWinner() {
    const w = event.participants.find(p => p.id === winnerPick);
    if (!w) { setErr('Chagua mshindi.'); return; }
    try { await declareLiveWinner(event.id, w.id, w.name); setShowWinner(false); flash(`${w.name} ni mshindi! 🏆`); onChange(); }
    catch (e) { setErr('Imeshindikana.'); }
  }

  async function doFinalize() {
    setBusy(true); setErr('');
    try {
      const authorName = await resolveAuthorName(currentUser);
      const out = await finalizeLiveEvent(event, { arbiterName, venue, author: { name: authorName } });
      setFinalizeResult(out);
      flash('Tukio limehamishiwa IFR.');
      onChange();
    } catch (e) { console.error(e); setErr('Imeshindikana kuhamisha tukio.'); }
    setBusy(false);
  }

  async function removeEvent() {
    if (!window.confirm(`Futa "${event.title}" kabisa? Hii haiathiri IFR rasmi kwa sababu bado halijahamishwa.`)) return;
    try { await deleteLiveEvent(event.id); flash('Tukio limefutwa.'); onChange(); }
    catch (e) { setErr('Imeshindikana kufuta.'); }
  }

  async function openHelperPicker() {
    setShowHelpers(true);
    if (staffList.length) return;
    try {
      const snap = await getDocs(collection(db, 'users'));
      setStaffList(snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.role === 'staff' || u.role === 'admin')
        .map(u => ({ id: u.id, name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email }))
        .sort((a, b) => a.name.localeCompare(b.name)));
    } catch (e) { setErr('Imeshindikana kupakia orodha ya staff.'); }
  }

  async function toggleHelper(s) {
    const already = (event.helperIds || []).includes(s.id);
    try {
      if (already) await removeHelper(event.id, s.id);
      else await addHelper(event.id, s.id, s.name);
      onChange();
    } catch (e) { setErr('Imeshindikana kubadilisha wasaidizi.'); }
  }

  const alreadyFinalized = !!event.finalizedEventId;

  return (
    <>
      <div style={{ ...L.card, borderLeft: `4px solid ${cat.color}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: T.serif, fontSize: '19px', color: T.green }}>{event.title}</div>
            <div style={{ fontSize: '11.5px', color: T.muted, marginTop: '4px', display: 'flex', gap: '7px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ ...L.pill, color: cat.color, background: `${cat.color}15` }}>{cat.label}</span>
              <span style={{ ...L.pill, color: T.muted, background: '#F3F1EB' }}>{pairingInfo?.label}</span>
              <span>Washindani {event.participants.length}</span>
              {(event.helperNames || []).length > 0 && <span>· Wasaidizi: {event.helperNames.join(', ')}</span>}
              {alreadyFinalized && <span style={{ color: '#059669', fontWeight: 700 }}>· Imehamishwa IFR ✓</span>}
            </div>
          </div>
          {event.status === 'finished' ? (
            <span style={{ ...L.pill, color: '#B45309', background: '#FEF3C7' }}>🏆 {event.winnerName}</span>
          ) : (
            <span style={{ ...L.pill, color: '#059669', background: '#ECFDF5' }}>Yanaendelea</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '9px', marginTop: '16px', flexWrap: 'wrap' }}>
          <button style={L.btnGhost} onClick={() => navigate(`/rating/live/${event.id}/print`)}>
            📄 Pakua Standings (PDF)
          </button>
          {!alreadyFinalized && canManage && event.status !== 'finished' && (
            <button style={L.btn} onClick={() => { setWinnerPick(standings[0]?.id || event.participants[0]?.id || ''); setShowWinner(true); }}>
              Tangaza Mshindi
            </button>
          )}
          {!alreadyFinalized && canManage && (
            <button style={{ ...L.btn, background: '#B45309' }} onClick={() => setShowFinalize(!showFinalize)}>
              📤 Maliza na Uhamishe kwenye IFR
            </button>
          )}
          {alreadyFinalized && (
            <button style={L.btnGhost} onClick={() => navigate('/rating/admin')}>Nenda Usimamizi wa IFR →</button>
          )}
          <button style={L.btnGhost} onClick={loadProjections} disabled={loadingProj}>
            {loadingProj ? 'Inahesabu…' : '📊 Onyesha Rating Inayotarajiwa'}
          </button>
          {isAdmin && (
            <button style={L.btnGhost} onClick={openHelperPicker}>
              👥 Wasaidizi ({(event.helperIds || []).length})
            </button>
          )}
          {isAdmin && !alreadyFinalized && (
            <button style={{ ...L.btnGhost, color: '#DC2626' }} onClick={removeEvent}>Futa tukio</button>
          )}
          {canManage && !alreadyFinalized && event.status !== 'finished' && event.pairingSystem === 'swiss' && (
            <button style={L.btnGhost} onClick={() => (showAddPlayer ? setShowAddPlayer(false) : openAddPlayer())}>
              {showAddPlayer ? 'Funga' : '+ Mchezaji Aliyechelewa'}
            </button>
          )}
        </div>

        {showAddPlayer && (
          <div style={{ background: '#FAF8F3', border: `1px solid ${T.line}`, borderRadius: '11px', padding: '14px', marginTop: '14px' }}>
            <input style={{ ...L.input, marginBottom: '9px' }} placeholder="Tafuta jina la mchezaji..."
              value={addSearch} onChange={e => setAddSearch(e.target.value)} />
            {addSearch.trim() && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: `1px solid ${T.line}`, borderRadius: '9px' }}>
                {filterPlayers(addAllPlayers, addSearch)
                  .filter(p => !event.participants.some(x => x.id === p.id))
                  .slice(0, 10)
                  .map(p => (
                    <div key={`${p.source}-${p.id}`} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '10px 12px', borderTop: `1px solid #F3F1EB` }}>
                      <div style={{ flex: 1, fontSize: '12.5px', fontWeight: 700, color: T.ink }}>{p.fullName}</div>
                      <button disabled={addBusy} style={{ ...L.btn, padding: '6px 12px', fontSize: '11px' }} onClick={() => addLatePlayer(p)}>+ Ongeza</button>
                    </div>
                  ))}
              </div>
            )}
          </div>
        )}

        {showHelpers && (
          <div style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '11px', padding: '14px', marginTop: '14px' }}>
            <label style={L.label}>Wasaidizi wa kuendesha tukio hili (admin bado ndiye kiongozi)</label>
            <div style={{ maxHeight: '220px', overflowY: 'auto', marginBottom: '11px' }}>
              {staffList.length === 0 ? (
                <div style={{ fontSize: '12px', color: T.muted }}>Inapakia...</div>
              ) : staffList.map(s => (
                <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '9px', padding: '7px 0', cursor: 'pointer', fontSize: '12.5px' }}>
                  <input type="checkbox" checked={(event.helperIds || []).includes(s.id)} onChange={() => toggleHelper(s)} />
                  {s.name}
                </label>
              ))}
            </div>
            <button style={{ ...L.btnGhost, padding: '9px 15px', fontSize: '12px' }} onClick={() => setShowHelpers(false)}>Funga</button>
          </div>
        )}

        {showWinner && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '11px', padding: '14px', marginTop: '14px' }}>
            <label style={L.label}>Mshindi</label>
            <select style={{ ...L.input, marginBottom: '11px' }} value={winnerPick} onChange={e => setWinnerPick(e.target.value)}>
              {[...event.participants].sort((a, b) => a.name.localeCompare(b.name)).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...L.btn, background: '#B45309', padding: '9px 15px', fontSize: '12px' }} onClick={confirmWinner}>Thibitisha</button>
              <button style={{ ...L.btnGhost, padding: '9px 15px', fontSize: '12px' }} onClick={() => setShowWinner(false)}>Ghairi</button>
            </div>
          </div>
        )}

        {showFinalize && !finalizeResult && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '11px', padding: '14px', marginTop: '14px' }}>
            <div style={{ fontSize: '12px', color: '#92400E', lineHeight: 1.7, marginBottom: '11px' }}>
              Hii itaunda tukio rasmi la IFR pamoja na michezo yote yenye matokeo. Baada ya hapo, nenda
              "Usimamizi wa IFR → Kuchapisha" kuhakiki na kuthibitisha rating mpya.
            </div>
            <label style={L.label}>Arbiter / msimamizi</label>
            <input style={{ ...L.input, marginBottom: '9px' }} value={arbiterName} onChange={e => setArbiterName(e.target.value)} placeholder={event.createdByName} />
            <label style={L.label}>Mahali</label>
            <input style={{ ...L.input, marginBottom: '12px' }} value={venue} onChange={e => setVenue(e.target.value)} />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ ...L.btn, background: '#B45309', opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={doFinalize}>
                {busy ? 'Inahamisha…' : 'Thibitisha Uhamishe'}
              </button>
              <button style={L.btnGhost} onClick={() => setShowFinalize(false)}>Ghairi</button>
            </div>
          </div>
        )}

        {finalizeResult && (
          <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: '11px', padding: '14px', marginTop: '14px', fontSize: '12.5px', color: '#065F46', lineHeight: 1.7 }}>
            ✓ Imefanikiwa — tukio la IFR limeundwa na michezo <strong>{finalizeResult.gamesCount}</strong> imeongezwa.
            <div style={{ marginTop: '9px' }}>
              <button style={{ ...L.btn, padding: '9px 15px', fontSize: '12px' }} onClick={() => navigate('/rating/admin')}>
                Nenda Usimamizi wa IFR →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Makadirio ya rating */}
      {projections && (
        <div style={L.card}>
          <h2 style={L.h2}>Rating Inayotarajiwa (makadirio, si rasmi)</h2>
          {event.participants.map(p => {
            const proj = projections[p.id];
            if (!proj) return null;
            const color = proj.change > 0 ? '#059669' : proj.change < 0 ? '#DC2626' : T.muted;
            return (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderTop: `1px solid #F3F1EB` }}>
                <div style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: T.ink }}>{p.name}</div>
                {proj.established ? (
                  <div style={{ fontSize: '13px', fontWeight: 700 }}>
                    {proj.baseline} → <span style={{ color }}>{proj.projected}</span>
                    {proj.change !== 0 && <span style={{ color, marginLeft: '5px' }}>({proj.change > 0 ? '+' : ''}{proj.change})</span>}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: T.muted }}>
                    {proj.projected ? `Inatarajiwa: ${proj.projected}` : (proj.note || 'Bado hana michezo ya kutosha')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Mechi za Sasa */}
      {latestRound && (
        <div style={L.card}>
          <h2 style={L.h2}>Mechi za Raundi {latestRound.number}</h2>
          <div style={{ display: 'grid', gap: '10px' }}>
            {latestRound.pairings.map((p, i) => (
              <MatchRow key={i} p={p} canEdit={canManage && event.status !== 'finished' && !alreadyFinalized}
                onResult={(res) => {
                  const updated = latestRound.pairings.map((x, j) => j === i ? { ...x, result: res } : x);
                  saveResultsForRound(latestRound.number, updated);
                }} />
            ))}
          </div>
          {latestRound.byeName && <div style={{ fontSize: '12px', color: T.muted, marginTop: '10px' }}>💤 Bye: {latestRound.byeName}</div>}

          {canManage && !alreadyFinalized && event.status !== 'finished' && (
            <div style={{ marginTop: '16px' }}>
              {event.pairingSystem === 'swiss' && !pairs && (
                <button style={L.btn} onClick={planSwissRound}>
                  <Icon name="sparkle" size={14} color="#fff" /> Panga Raundi Ijayo (Swiss)
                </button>
              )}
              {isKnockout && !isFinal && latestRound.pairings.every(p => p.result || p.isBye) && (
                <button style={L.btn} onClick={doAdvanceKnockout} disabled={busy}>Pandisha Washindi → Raundi Ijayo</button>
              )}
              {isKnockout && isFinal && latestRound.pairings[0].result && (
                <div style={{ ...L.ok }}>
                  🏆 Fainali imekamilika — <strong>{latestRound.pairings[0].result === '1-0' ? latestRound.pairings[0].whiteName : latestRound.pairings[0].blackName}</strong> ndiye mshindi. Bofya "Tangaza Mshindi" hapo juu.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {pairs && (
        <div style={L.card}>
          <h2 style={L.h2}>Hakiki Raundi Iliyopangwa</h2>
          {pairs.map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: '7px', alignItems: 'center', marginBottom: '9px', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, fontSize: '13px', fontWeight: 700 }}>{p.whiteName} <span style={{ fontSize: '10px', color: T.muted }}>(Weupe)</span></div>
              <select style={{ ...L.input, flex: '0 1 110px' }} value={p.result} onChange={e => updatePair(i, 'result', e.target.value)}>
                {RESULTS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
              <div style={{ flex: 1, fontSize: '13px', fontWeight: 700, textAlign: 'right' }}><span style={{ fontSize: '10px', color: T.muted }}>(Weusi) </span>{p.blackName}</div>
            </div>
          ))}
          {byeId && <div style={{ fontSize: '12px', color: T.muted, marginBottom: '11px' }}>💤 Bye: {event.participants.find(x => x.id === byeId)?.name}</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button style={{ ...L.btn, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={saveSwissRound}>Chapisha Raundi</button>
            <button style={L.btnGhost} onClick={() => setPairs(null)}>Ghairi</button>
          </div>
        </div>
      )}

      {/* Standings (Swiss / Round Robin) */}
      {!isKnockout && (
        <div style={L.card}>
          <h2 style={L.h2}>Standings</h2>
          {standings.every(s => s.games === 0) ? (
            <div style={L.empty}>Bado hakuna matokeo.</div>
          ) : standings.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 0', borderTop: i ? `1px solid #F3F1EB` : 'none' }}>
              <div style={{ width: '24px', textAlign: 'center', fontSize: '13px', fontWeight: 800, color: i === 0 ? '#B45309' : T.muted }}>
                {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
              </div>
              <div style={{ flex: 1, fontSize: '13.5px', fontWeight: 700, color: T.ink }}>{s.name}</div>
              <div style={{ fontSize: '10.5px', color: T.muted }}>W{s.wins} S{s.draws} L{s.losses}</div>
              <div style={{ fontSize: '17px', fontWeight: 800, color: T.green }}>{s.points}</div>
            </div>
          ))}
        </div>
      )}

      {/* Historia ya raundi */}
      {rounds.length > 1 && (
        <div style={L.card}>
          <h2 style={L.h2}>Raundi Zilizopita</h2>
          {[...rounds].slice(0, -1).reverse().map(r => (
            <div key={r.number} style={{ marginBottom: '14px' }}>
              <div style={{ fontSize: '12px', fontWeight: 800, color: T.green, marginBottom: '7px' }}>Raundi {r.number}</div>
              {r.pairings.map((p, i) => (
                <div key={i} style={{ fontSize: '12.5px', padding: '5px 0', color: T.ink }}>
                  {p.whiteName} <span style={{ color: T.muted }}>{p.result || 'bado'}</span> {p.blackName}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function MatchRow({ p, canEdit, onResult }) {
  const pending = !p.result;
  if (p.isBye) {
    return (
      <div style={{ padding: '12px 15px', background: '#FAF8F3', borderRadius: '12px', fontSize: '13px', color: T.muted, textAlign: 'center' }}>
        💤 {p.whiteName} — BYE (anaendelea moja kwa moja)
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '13px 15px', background: pending ? '#FAF8F3' : '#fff', border: `1px solid ${pending ? T.line : '#D1FAE5'}`, borderRadius: '13px', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: '90px', fontSize: '13px', fontWeight: 700, color: T.ink }}>{p.whiteName} <span style={{ fontSize: '10px', color: T.muted }}>(Weupe)</span></div>
      {canEdit ? (
        <select style={{ ...L.input, flex: '0 1 110px' }} value={p.result} onChange={e => onResult(e.target.value)}>
          {RESULTS.map(r => <option key={r.v} value={r.v}>{r.l}</option>)}
        </select>
      ) : (
        <div style={{ fontSize: '12px', fontWeight: 800, color: pending ? '#D97706' : T.green, minWidth: '46px', textAlign: 'center' }}>
          {pending ? 'BADO' : p.result}
        </div>
      )}
      <div style={{ flex: 1, minWidth: '90px', fontSize: '13px', fontWeight: 700, color: T.ink, textAlign: 'right' }}><span style={{ fontSize: '10px', color: T.muted }}>(Weusi) </span>{p.blackName}</div>
    </div>
  );
}
